import { createStart, createMiddleware } from "@tanstack/react-start";
import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { safeLocalStorageGet } from "@/lib/safe-browser";
import { isNativeApp } from "@/lib/native-platform";
import { getSecureSessionToken } from "@/lib/native-secure-storage";

function readSupabaseAccessTokenSync() {
  if (typeof window === "undefined") return null;
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const storageKeys = supabaseUrl
      ? [`sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`]
      : [];

    for (const key of storageKeys) {
      const raw = safeLocalStorageGet(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const session = parsed?.currentSession ?? parsed?.session ?? parsed;
      if (!session?.access_token) continue;
      if (session.expires_at && session.expires_at * 1000 < Date.now()) continue;
      return session.access_token as string;
    }
  } catch {
    return null;
  }
  return null;
}

const attachCachedSupabaseAuth = createMiddleware({ type: "function" }).client(async ({ next }) => {
  const token = readSupabaseAccessTokenSync();
  return next({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
});

/**
 * Native wrapper: reads the signed identity token from Keychain/Keystore instead of
 * localStorage. In web/PWA mode this middleware is a no-op so the existing
 * Supabase/localStorage flow remains untouched.
 */
const attachNativeSecureSupabaseAuth = createMiddleware({ type: "function" }).client(async ({ next }) => {
  if (!isNativeApp()) {
    return next();
  }
  const token = await getSecureSessionToken();
  return next({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
});

/**
 * "Invalid server function ID" não é uma falha do servidor: é um browser (ou
 * cache da PWA) com um bundle antigo a chamar um ID que já não existe no
 * manifesto da build atual. Respondemos 409 com um código próprio para o
 * cliente se recuperar, e não poluímos o painel do fundador com erros 500.
 */
function isStaleServerFunctionError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String((error as { message?: unknown } | null)?.message ?? "");
  return /invalid server function id/i.test(message);
}

const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    if (isStaleServerFunctionError(error)) {
      return Response.json(
        { error: "Invalid server function ID", code: "stale_build" },
        {
          status: 409,
          headers: {
            "cache-control": "no-store, no-cache, must-revalidate, max-age=0",
            "x-routify-stale-build": "1",
          },
        },
      );
    }
    console.error(error);
    // Fire-and-forget: persist for the founder dashboard.
    try {
      const { logServerError } = await import("@/lib/error-logger.server");
      const err = error as any;
      const isServerFn = url.pathname.startsWith("/_serverFn");
      void logServerError({
        source: isServerFn ? "serverfn" : "server",
        message: err?.message ? String(err.message) : String(error),
        stack: err?.stack ? String(err.stack) : null,
        url: url.pathname + url.search,
        userAgent: request.headers.get("user-agent"),
        status: 500,
        context: { method: request.method },
      });
    } catch { /* ignore */ }

    if (url.pathname.startsWith("/_serverFn") || url.pathname.startsWith("/api/")) {
      return Response.json(
        { error: "O pedido falhou. Tenta novamente." },
        {
          status: 500,
          headers: {
            "cache-control": "no-store, no-cache, must-revalidate, max-age=0",
          },
        },
      );
    }
    return new Response(renderErrorPage(), {
      status: 500,
      headers: {
        "content-type": "text/html; charset=utf-8",
        "cache-control": "no-store, no-cache, must-revalidate, max-age=0",
        "pragma": "no-cache",
        "expires": "0",
      },
    });
  }
});

export const startInstance = createStart(() => ({
  requestMiddleware: [errorMiddleware],
  functionMiddleware: [attachSupabaseAuth, attachCachedSupabaseAuth, attachNativeSecureSupabaseAuth],
}));
